import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { DollarSign, Clock, FileEdit, Download, Trash2, Loader2, AlertTriangle, X, FileSpreadsheet, Search, Settings, Mail, History, Lock } from 'lucide-react';
import { api } from '../utils/apiClient';
import { OPCIONES_CIERRE_MASTER, OPCIONES_TIPO_SERVICIO } from '../constants/masterData';
import { addLog } from '../utils/logger';
import AuditLogModal from './AuditLogModal';

// ===============================================================
// HELPERS
// ===============================================================
const getStatusColor = (status) => {
  if (!status) return 'bg-blue-900 text-blue-300';
  const lowerStatus = String(status).toLowerCase();
  if (lowerStatus.includes('exitoso') || lowerStatus.includes('completado') || lowerStatus.includes('realizada') || lowerStatus.includes('simple')) return 'bg-green-900 text-green-300';
  if (lowerStatus.includes('cancelado') || lowerStatus.includes('rechazado') || lowerStatus.includes('fallido')) return 'bg-red-900 text-red-300';
  if (lowerStatus.includes('pendiente') || lowerStatus.includes('proceso') || lowerStatus.includes('sitio') || lowerStatus.includes('preliminar')) return 'bg-yellow-900 text-yellow-300';
  return 'bg-blue-900 text-blue-300';
};

const formatCurrency = (value) => {
  const numericValue = Number(value);
  if (isNaN(numericValue)) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(numericValue);
};

const normalizeString = (str) => {
  if (!str) return '';
  try {
    return str.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  } catch (error) {
    return String(str).toLowerCase();
  }
};

const CardRow = ({ label, value, children, className = '' }) => (
  <div className={`flex flex-col sm:flex-row sm:justify-between ${className}`}>
    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    {value ? (
      <span className="text-xs text-slate-100 font-medium sm:text-right truncate tabular-nums">{value}</span>
    ) : (
      <div className="sm:text-right">{children}</div>
    )}
  </div>
);

// ===============================================================
// MODAL DE HONORARIOS
// ===============================================================
const CAMPOS_HON = ['honorario_abogado_local', 'honorario_abogado_foraneo', 'honorario_firma'];
const LABELS_HON = { honorario_abogado_local: 'Hon. Abg. Local', honorario_abogado_foraneo: 'Hon. Abg. Foráneo', honorario_firma: 'Hon. Firma' };

const HonorariosModal = ({ onClose, clientesConfig }) => {
  const [slugSeleccionado, setSlugSeleccionado] = useState('');
  const [pestana, setPestana] = useState('futuro'); // 'futuro' | 'rango'
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);
  const [nuevaTarifa, setNuevaTarifa] = useState('');
  // "Aplica desde" opcional para pestana futuro:
  // - vacio  = comportamiento clasico (aplica retroactivo a todos los casos sin honorario manual)
  // - fecha  = solo casos creados a partir de esa fecha; anteriores conservan tarifa vieja
  const [aplicaDesde, setAplicaDesde] = useState('');

  // Estado para la pestaña de rango
  const [rango, setRango] = useState({
    tarifa: '', fecha_ini: '', fecha_fin: '',
    honorario_abogado_local: 0, honorario_abogado_foraneo: 0, honorario_firma: 0,
  });
  const [savingRango, setSavingRango] = useState(false);
  const [msgRango, setMsgRango] = useState(null);

  const cargarHonorarios = useCallback(async (slug) => {
    if (!slug) return;
    setLoading(true);
    setMsg(null);
    try {
      const data = await api.get(`/tabla-honorarios/${slug}`);
      setFilas(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'No se pudo cargar la tabla de honorarios.' });
      setFilas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slugSeleccionado) cargarHonorarios(slugSeleccionado);
    else setFilas([]);
    setMsg(null);
    setMsgRango(null);
    setRango(r => ({ ...r, tarifa: '' }));
  }, [slugSeleccionado, cargarHonorarios]);

  // Sync rango.tarifa cuando cargan filas y no hay tarifa seleccionada
  useEffect(() => {
    if (filas.length > 0 && !rango.tarifa) {
      setRango(r => ({ ...r, tarifa: filas[0].tarifa }));
    }
  }, [filas]);

  const handleChange = (idx, campo, valor) => {
    setFilas(prev => prev.map((f, i) => i === idx ? { ...f, [campo]: valor } : f));
  };

  const handleGuardar = async (fila) => {
    setSaving(fila.tarifa);
    setMsg(null);
    try {
      const payload = {
        tarifa: fila.tarifa,
        honorario_abogado_local: parseFloat(fila.honorario_abogado_local) || 0,
        honorario_abogado_foraneo: parseFloat(fila.honorario_abogado_foraneo) || 0,
        honorario_firma: parseFloat(fila.honorario_firma) || 0,
      };
      if (aplicaDesde) payload.aplica_desde = aplicaDesde;
      await api.put(`/tabla-honorarios/${slugSeleccionado}`, payload);
      const sufijo = aplicaDesde
        ? ` (aplica desde ${aplicaDesde}; casos anteriores conservan tarifa vieja)`
        : ' (registros existentes congelados al nuevo valor)';
      setMsg({ tipo: 'ok', texto: `Tarifa "${fila.tarifa}" guardada${sufijo}.` });
      addLog(`Tarifa "${fila.tarifa}" actualizada para ${slugSeleccionado}${aplicaDesde ? ' desde ' + aplicaDesde : ''}`, 'success', 'Honorarios');
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'Error al guardar.' });
      addLog(`Error al guardar tarifa "${fila.tarifa}"`, 'error', 'Honorarios');
    } finally {
      setSaving(null);
    }
  };

  const handleAgregarTarifa = () => {
    const nombre = nuevaTarifa.trim().toUpperCase();
    if (!nombre) return;
    if (filas.find(f => f.tarifa === nombre)) {
      setMsg({ tipo: 'error', texto: `La tarifa "${nombre}" ya existe.` });
      return;
    }
    setFilas(prev => [...prev, { tarifa: nombre, honorario_abogado_local: 0, honorario_abogado_foraneo: 0, honorario_firma: 0 }]);
    setNuevaTarifa('');
    setMsg(null);
  };

  const handleGuardarRango = async () => {
    if (!rango.tarifa || !rango.fecha_ini || !rango.fecha_fin) {
      setMsgRango({ tipo: 'error', texto: 'Completa tarifa, fecha inicio y fecha fin.' });
      return;
    }
    if (rango.fecha_ini > rango.fecha_fin) {
      setMsgRango({ tipo: 'error', texto: 'La fecha inicio debe ser anterior o igual a la fecha fin.' });
      return;
    }
    setSavingRango(true);
    setMsgRango(null);
    try {
      const res = await api.put(`/tabla-honorarios/${slugSeleccionado}/rango`, {
        tarifa: rango.tarifa,
        fecha_ini: rango.fecha_ini,
        fecha_fin: rango.fecha_fin,
        honorario_abogado_local: parseFloat(rango.honorario_abogado_local) || 0,
        honorario_abogado_foraneo: parseFloat(rango.honorario_abogado_foraneo) || 0,
        honorario_firma: parseFloat(rango.honorario_firma) || 0,
      });
      const filas_afectadas = res?.filas_afectadas ?? res?.data?.filas_afectadas ?? 0;
      setMsgRango({ tipo: 'ok', texto: `${filas_afectadas} registro(s) actualizado(s) en el rango.` });
      addLog(`Honorarios actualizados por rango — ${filas_afectadas} registro(s) en ${slugSeleccionado}`, 'success', 'Honorarios');
    } catch (e) {
      setMsgRango({ tipo: 'error', texto: 'Error al aplicar el cambio por rango.' });
      addLog(`Error al aplicar cambio por rango en ${slugSeleccionado}`, 'error', 'Honorarios');
    } finally {
      setSavingRango(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/50 to-slate-900 rounded-t-3xl flex items-center justify-between flex-none">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Settings size={18} className="text-blue-400" /> Gestión de Honorarios</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="p-6 flex flex-col gap-4 overflow-hidden flex-1 min-h-0">
          {/* Selector aseguradora */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Aseguradora</label>
            <select value={slugSeleccionado} onChange={e => setSlugSeleccionado(e.target.value)} className={inputCls}>
              <option value="">Seleccionar aseguradora...</option>
              {clientesConfig.map(c => (
                <option key={c.slug} value={c.slug}>{c.nombre_display || c.nombre}</option>
              ))}
            </select>
          </div>

          {/* Pestañas */}
          {slugSeleccionado && (
            <div className="flex rounded-xl bg-slate-800/60 border border-slate-700/50 p-1 gap-1 flex-none">
              {[
                { id: 'futuro', label: 'A Futuro' },
                { id: 'rango',  label: 'Por Rango de Fecha' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setPestana(tab.id); setMsg(null); setMsgRango(null); }}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    pestana === tab.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* ── PESTAÑA: A FUTURO ── */}
          {(!slugSeleccionado || pestana === 'futuro') && (
            <div className="flex flex-col gap-3 overflow-hidden flex-1 min-h-0">
              {slugSeleccionado && (
                <div className="flex gap-2 flex-none">
                  <input
                    type="text"
                    value={nuevaTarifa}
                    onChange={e => setNuevaTarifa(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAgregarTarifa()}
                    placeholder="Nueva tarifa (ej: TELEFONICO)"
                    className="flex-1 px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 text-sm placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors"
                  />
                  <button onClick={handleAgregarTarifa} className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
                    + Agregar
                  </button>
                </div>
              )}

              {/* Aplica desde (opcional) — corte fecha+hora. Casos del mismo dia
                  sin hora_asignacion se consideran POST-corte (heredan tarifa nueva). */}
              {slugSeleccionado && filas.length > 0 && (
                <div className="flex-none flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-800/40 border border-slate-700 flex-wrap">
                  <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap">Aplica desde</label>
                  <input
                    type="datetime-local"
                    value={aplicaDesde}
                    onChange={e => setAplicaDesde(e.target.value)}
                    className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                  />
                  {aplicaDesde && (
                    <button
                      onClick={() => setAplicaDesde('')}
                      className="text-xs text-slate-400 hover:text-white underline"
                    >limpiar</button>
                  )}
                  <span className="text-xs text-slate-500 leading-tight flex-1 min-w-[200px]">
                    {aplicaDesde
                      ? <>Casos asignados desde <span className="text-amber-400 font-semibold">{aplicaDesde.replace('T', ' ')}</span> hereden tarifa nueva. Anteriores conservan tarifa vieja. Casos sin hora del mismo día se cuentan como posteriores.</>
                      : <>Vacío = aplica retroactivo a todos los casos sin honorario manual.</>}
                  </span>
                </div>
              )}

              {msg && (
                <div className={`px-4 py-2 rounded-xl text-sm font-medium flex-none ${msg.tipo === 'ok' ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/50' : 'bg-red-900/40 text-red-300 border border-red-700/50'}`}>
                  {msg.texto}
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 size={28} className="animate-spin text-blue-400" /></div>
                ) : !slugSeleccionado ? (
                  <div className="flex items-center justify-center py-12 text-slate-500 text-sm">Selecciona una aseguradora para ver los honorarios.</div>
                ) : filas.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-slate-500 text-sm">Sin tarifas configuradas. Agrega una usando el campo de arriba.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Tarifa</th>
                        {CAMPOS_HON.map(c => <th key={c} className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{LABELS_HON[c]}</th>)}
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filas.map((fila, idx) => (
                        <tr key={fila.tarifa} className="hover:bg-slate-800/40 transition-colors">
                          <td className="px-3 py-2 text-slate-200 font-medium">{fila.tarifa}</td>
                          {CAMPOS_HON.map(campo => (
                            <td key={campo} className="px-3 py-2">
                              <input
                                type="number"
                                value={fila[campo] ?? 0}
                                onChange={e => handleChange(idx, campo, e.target.value)}
                                className="w-full px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500 transition-colors"
                                step="1000"
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            <button
                              onClick={() => handleGuardar(fila)}
                              disabled={saving === fila.tarifa}
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                            >
                              {saving === fila.tarifa ? <Loader2 size={12} className="animate-spin" /> : null}
                              Guardar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ── PESTAÑA: POR RANGO DE FECHA ── */}
          {slugSeleccionado && pestana === 'rango' && (
            <div className="flex flex-col gap-4 overflow-auto flex-1 min-h-0">
              <p className="text-xs text-slate-500 leading-relaxed">
                Modifica los honorarios de registros <span className="text-amber-400 font-semibold">ya existentes</span> filtrando por tarifa y rango de fecha. Solo afecta los casos que coincidan con los criterios seleccionados.
              </p>

              {/* Tarifa */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tarifa</label>
                <select
                  value={rango.tarifa}
                  onChange={e => setRango(r => ({ ...r, tarifa: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Seleccionar tarifa...</option>
                  {filas.map(f => <option key={f.tarifa} value={f.tarifa}>{f.tarifa}</option>)}
                </select>
              </div>

              {/* Rango de fechas */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Fecha Inicio</label>
                  <input
                    type="date"
                    value={rango.fecha_ini}
                    onChange={e => setRango(r => ({ ...r, fecha_ini: e.target.value }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Fecha Fin</label>
                  <input
                    type="date"
                    value={rango.fecha_fin}
                    onChange={e => setRango(r => ({ ...r, fecha_fin: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Honorarios */}
              <div className="grid grid-cols-3 gap-3">
                {CAMPOS_HON.map(campo => (
                  <div key={campo}>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{LABELS_HON[campo]}</label>
                    <input
                      type="number"
                      value={rango[campo] ?? 0}
                      onChange={e => setRango(r => ({ ...r, [campo]: e.target.value }))}
                      className={inputCls}
                      step="1000"
                    />
                  </div>
                ))}
              </div>

              {msgRango && (
                <div className={`px-4 py-2 rounded-xl text-sm font-medium ${msgRango.tipo === 'ok' ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/50' : 'bg-red-900/40 text-red-300 border border-red-700/50'}`}>
                  {msgRango.texto}
                </div>
              )}

              <button
                onClick={handleGuardarRango}
                disabled={savingRango}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 shadow-lg shadow-amber-900/20"
              >
                {savingRango ? <Loader2 size={16} className="animate-spin" /> : null}
                Aplicar a registros en el rango
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===============================================================
// MODAL DE EDICIÓN
// ===============================================================
const EditInvoiceModal = ({ invoice, onSave, onClose, clientesConfig, abogados }) => {
  const [formData, setFormData] = useState({
    honorario_abogado: 0,
    honorario_firma: 0,
    cierre_servicio: '',
    tipo_servicio: '',
    abogado: '',
    kilometraje: 0,
    liberacion: '',
    fecha_sesion: '',
  });
  const [opcionesFiltradas, setOpcionesFiltradas] = useState([]);
  const [opcionesTipo, setOpcionesTipo] = useState([]);

  useEffect(() => {
    if (!invoice) return;

    // Resolución del cierre/tipo se hará en un useEffect aparte que espera
    // a que lleguen las opciones dinámicas del catálogo de la aseguradora.
    // Aquí solo inicializamos lo que NO depende de catálogo.
    let idCierreStr = '';
    let tipoLabel = '';

    // Resolver cédula del abogado: si el valor almacenado es nombre, buscar num_doc
    let abogadoCedula = invoice.abogado || '';
    if (abogadoCedula && abogados && abogados.length > 0) {
      const porCedula = abogados.find(a => String(a.num_doc) === String(abogadoCedula));
      if (!porCedula) {
        // El valor es nombre, buscar su cédula
        const porNombre = abogados.find(a => a.nombre === abogadoCedula);
        if (porNombre) abogadoCedula = String(porNombre.num_doc);
      }
    }

    setFormData({
      honorario_abogado: invoice.honorario_abogado || 0,
      honorario_firma: invoice.honorario_firma || 0,
      cierre_servicio: idCierreStr,
      tipo_servicio: tipoLabel,
      abogado: abogadoCedula,
      kilometraje: invoice.kilometraje ?? 0,
      liberacion: invoice.liberacion || '',
      fecha_sesion: invoice.fecha_sesion ? invoice.fecha_sesion.replace(' ', 'T') : '',
    });

    const cliente = (invoice.cliente || '').toUpperCase();
    const esIKE = cliente.includes('IKE');
    const esASIS = cliente.includes('ASIS') || cliente.includes('SPREX');

    // Opciones de tipo_servicio filtradas por cliente
    const tiposFiltrados = OPCIONES_TIPO_SERVICIO.filter(opt => {
      if (opt.grupo === 'COMUN') return true;
      if (esIKE && opt.grupo === 'IKE') return true;
      if (esASIS && opt.grupo === 'ASIS') return true;
      return !esIKE && !esASIS;
    });
    setOpcionesTipo(tiposFiltrados);

    const loadOpciones = async () => {
      const nombreAseg = (invoice.cliente || '').trim().toLowerCase();
      const clienteMatch = (clientesConfig || []).find(c => {
        const nombreConfig = (c.nombre_display || c.nombre || '').trim().toLowerCase();
        return nombreConfig === nombreAseg || nombreAseg.includes(nombreConfig) || nombreConfig.includes(nombreAseg);
      });

      if (clienteMatch && clienteMatch.id) {
        try {
          const opciones = await api.get(`/clientes-config/${clienteMatch.id}/opciones`);
          if (opciones?.cierre_servicio) {
            setOpcionesFiltradas(opciones.cierre_servicio);
            return;
          }
        } catch (e) {
          console.warn('[EditInvoiceModal] Error al cargar opciones dinámicas:', e);
        }
      }

      const filtradas = OPCIONES_CIERRE_MASTER.filter(opt => {
        if (opt.grupo === 'COMUN') return true;
        if (esIKE && opt.grupo === 'IKE') return true;
        if (esASIS && opt.grupo === 'ASIS') return true;
        return !esIKE && !esASIS;
      });
      setOpcionesFiltradas(filtradas);
    };

    loadOpciones();
    // deps: solo expediente/cliente y longitud de abogados. Si dejamos
    // clientesConfig/abogados como objetos enteros, el padre los recrea como
    // referencias nuevas en cada render -> el effect se dispara -> setFormData
    // resetea lo que el usuario esta escribiendo (input pierde focus). Por eso
    // solo deps "estables" (primitivos).
  }, [invoice?.expediente, invoice?.cliente, abogados?.length]);

  // useEffect adicional: cuando llegan opciones dinamicas del catalogo de la
  // aseguradora, resolver el cierre/tipo del invoice usando ESAS opciones (no
  // OPCIONES_CIERRE_MASTER que tiene IKE+ASIS mezclados y resuelve mal IDs).
  useEffect(() => {
    if (!invoice) return;
    if (!opcionesFiltradas?.length && !opcionesTipo?.length) return;

    let nuevoCierre = formData.cierre_servicio;
    let nuevoTipo = formData.tipo_servicio;

    // Resolver cierre desde id_cierre_servicio (si viene), o desde label.
    const idDirectoCierre = invoice.id_cierre_servicio ?? invoice.id_cierre_se;
    if (idDirectoCierre != null && idDirectoCierre !== '') {
      const found = (opcionesFiltradas || []).find(o => String(o.id) === String(idDirectoCierre));
      if (found) nuevoCierre = String(found.id);
    } else {
      const labelInv = String(invoice.cierre_servicio || invoice.estado || '').trim().toUpperCase();
      if (labelInv) {
        const parsed = parseInt(labelInv, 10);
        if (!isNaN(parsed)) {
          // Es un ID numerico: usar SOLO si existe en opciones de ESTA aseguradora
          const found = (opcionesFiltradas || []).find(o => String(o.id) === String(parsed));
          if (found) nuevoCierre = String(found.id);
          else nuevoCierre = ''; // ID de otra aseguradora -> no asumir
        } else {
          // Es un label: buscar en opciones de ESTA aseguradora
          const found = (opcionesFiltradas || []).find(o => String(o.label).trim().toUpperCase() === labelInv);
          if (found) nuevoCierre = String(found.id);
        }
      }
    }

    // Resolver tipo desde id_tipo_servicio (si viene), o desde label
    const idDirectoTipo = invoice.id_tipo_servicio;
    if (idDirectoTipo != null && idDirectoTipo !== '') {
      const found = (opcionesTipo || []).find(o => String(o.id) === String(idDirectoTipo));
      if (found) nuevoTipo = found.label;
    } else {
      const labelInv = String(invoice.tipo_servicio || '').trim().toUpperCase();
      if (labelInv && labelInv !== 'N/A') {
        const parsed = parseInt(labelInv, 10);
        if (!isNaN(parsed)) {
          const found = (opcionesTipo || []).find(o => String(o.id) === String(parsed));
          if (found) nuevoTipo = found.label;
        } else {
          const found = (opcionesTipo || []).find(o => String(o.label).trim().toUpperCase() === labelInv);
          if (found) nuevoTipo = found.label;
        }
      }
    }

    // Resolver abogado: si invoice.abogado parece un nombre y no una cedula,
    // buscar en lista de abogados.
    let nuevoAbogado = formData.abogado;
    const abogRaw = invoice.abogado || '';
    if (abogRaw && abogados && abogados.length > 0) {
      const yaEsCedula = (abogados || []).find(a => String(a.num_doc) === String(abogRaw));
      if (yaEsCedula) {
        nuevoAbogado = String(yaEsCedula.num_doc);
      } else {
        const porNombre = (abogados || []).find(a =>
          String(a.nombre || '').trim().toUpperCase() === String(abogRaw).trim().toUpperCase()
        );
        if (porNombre) nuevoAbogado = String(porNombre.num_doc);
      }
    }

    if (nuevoCierre !== formData.cierre_servicio || nuevoTipo !== formData.tipo_servicio || nuevoAbogado !== formData.abogado) {
      setFormData(prev => ({ ...prev, cierre_servicio: nuevoCierre, tipo_servicio: nuevoTipo, abogado: nuevoAbogado }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opcionesFiltradas, opcionesTipo, invoice?.expediente, abogados?.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-gray-800 text-gray-100 rounded-2xl shadow-xl w-full max-w-md mx-auto p-6 relative max-h-[90vh] overflow-y-auto border border-gray-700" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-100 hover:bg-gray-700 rounded-full transition-colors"><X size={20} /></button>
        <h2 className="text-2xl font-bold mb-6 text-blue-400">Editar Factura: {invoice.expediente}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Abogado (select) */}
          <div>
            <label htmlFor="abogado" className="block text-sm font-medium text-gray-300 mb-1">Abogado</label>
            <select
              id="abogado"
              name="abogado"
              value={formData.abogado}
              onChange={handleChange}
              className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">Sin asignar</option>
              {(abogados || []).map(a => (
                <option key={a.id} value={String(a.num_doc)}>{a.nombre}</option>
              ))}
            </select>
          </div>

          {/* Honorarios */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="honorario_abogado" className="block text-sm font-medium text-gray-300 mb-1">Honorario Abogado</label>
              <input type="number" id="honorario_abogado" name="honorario_abogado" value={formData.honorario_abogado} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none" step="1000" />
            </div>
            <div>
              <label htmlFor="honorario_firma" className="block text-sm font-medium text-gray-300 mb-1">Honorario Firma</label>
              <input type="number" id="honorario_firma" name="honorario_firma" value={formData.honorario_firma} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none" step="1000" />
            </div>
          </div>

          {/* Kilometraje */}
          <div>
            <label htmlFor="kilometraje" className="block text-sm font-medium text-gray-300 mb-1">Kilometraje</label>
            <input type="number" id="kilometraje" name="kilometraje" value={formData.kilometraje} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none" step="1000" min="0" />
          </div>

          {/* Tipo de servicio */}
          <div>
            <label htmlFor="tipo_servicio" className="block text-sm font-medium text-gray-300 mb-1">Tipo de Servicio</label>
            <select id="tipo_servicio" name="tipo_servicio" value={formData.tipo_servicio} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
              <option value="">Sin asignar</option>
              {opcionesTipo.map(opt => (<option key={opt.id} value={opt.label}>{opt.label}</option>))}
            </select>
          </div>

          {/* Estado del servicio */}
          <div>
            <label htmlFor="cierre_servicio" className="block text-sm font-medium text-gray-300 mb-1">Estado del Servicio ({invoice.cliente || 'N/A'})</label>
            <select id="cierre_servicio" name="cierre_servicio" value={formData.cierre_servicio} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
              <option value="">Seleccionar estado...</option>
              {opcionesFiltradas.map(opt => (<option key={opt.id} value={String(opt.id)}>{opt.label}</option>))}
            </select>
          </div>

          {/* Liberación */}
          <div>
            <label htmlFor="liberacion" className="block text-sm font-medium text-gray-300 mb-1">Liberación</label>
            <input type="text" id="liberacion" name="liberacion" value={formData.liberacion} onChange={handleChange} className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="Número o descripción de liberación..." />
          </div>

          {/* Fecha Audiencia / Liberación */}
          <div>
            <label htmlFor="fecha_sesion" className="block text-sm font-medium text-gray-300 mb-1">Fecha Audiencia / Liberación</label>
            <input
              type="datetime-local"
              id="fecha_sesion"
              name="fecha_sesion"
              value={formData.fecha_sesion}
              onChange={handleChange}
              step="60"
              className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700 mt-6">
            <button type="button" onClick={onClose} className="py-2 px-5 bg-gray-600 hover:bg-gray-500 text-white font-semibold rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="py-2 px-5 bg-blue-700 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors shadow-lg">Guardar Cambios</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===============================================================
// TABLA DE FACTURAS
// ===============================================================
const InvoicesTable = ({ invoices, allInvoices, onEdit, onDelete, onGenerateSingle, onHistory, generatingIds, selectedIds, setSelectedIds }) => {
  const selectablePool = (allInvoices && allInvoices.length > 0) ? allInvoices : invoices;
  const handleSelectAll = (e) => { setSelectedIds(e.target.checked ? selectablePool.map(inv => inv.id) : []); };
  const handleSelectOne = (id) => { setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]); };

  return (
    <div className="h-full flex flex-col">
      {/* Vista móvil */}
      <div className="md:hidden flex-1 overflow-y-auto p-1 space-y-3">
        {invoices.length === 0 ? (<div className="text-center text-slate-500 py-12 text-sm">No hay facturas que coincidan.</div>) : (
          invoices.map((invoice) => (
            <div key={invoice.id} className={`bg-slate-900/80 rounded-2xl p-4 shadow-xl border transition-colors ${selectedIds.includes(invoice.id) ? 'border-blue-500/60 ring-1 ring-blue-500/40' : 'border-slate-700/50'}`}>
              <div className="flex justify-between items-start pb-3 mb-3 border-b border-slate-800">
                <div className="flex items-center space-x-3">
                  <input type="checkbox" checked={selectedIds.includes(invoice.id)} onChange={() => handleSelectOne(invoice.id)} className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500/40 focus:ring-offset-0" />
                  <div><span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Expediente</span><h3 className="text-lg font-semibold text-blue-300 tabular-nums">{invoice.expediente}</h3></div>
                </div>
                <div className="flex items-center gap-1">
                  {invoice.pagado_en ? (
                    <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] font-semibold px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full" title={`Pagado el ${new Date(invoice.pagado_en).toLocaleDateString('es-CO')}${invoice.pagado_por_nickname ? ' por ' + invoice.pagado_por_nickname : ''}`}>
                      <Lock size={12} /> Pagado
                    </span>
                  ) : (
                    <>
                      <button onClick={() => onEdit(invoice)} className="p-2 rounded-lg text-blue-300 hover:text-white hover:bg-blue-500/15 transition-colors"><FileEdit size={16} /></button>
                      <button onClick={() => onDelete(invoice)} className="p-2 rounded-lg text-rose-400 hover:text-white hover:bg-rose-500/15 transition-colors"><Trash2 size={16} /></button>
                    </>
                  )}
                  <button onClick={() => onHistory(invoice)} className="p-2 rounded-lg text-violet-300 hover:text-white hover:bg-violet-500/15 transition-colors" title="Ver historial"><History size={16} /></button>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <CardRow label="Abogado" value={invoice.abogado || '—'} />
                <CardRow label="Cliente" value={invoice.cliente} />
                <CardRow label="Monto" value={invoice.monto} />
                <CardRow label="Kilometraje" value={formatCurrency(invoice.kilometraje ?? 0)} />
                <CardRow label="Liberación" value={invoice.liberacion || '—'} />
                <CardRow label="Fecha" value={invoice.vencimiento} />
              </div>
              <div className="pt-2 border-t border-slate-800 flex justify-between items-center">
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${invoice.estadoColor}`}>{invoice.estado}</span>
                <button onClick={() => onGenerateSingle(invoice)} disabled={generatingIds.includes(invoice.id)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50">
                  {generatingIds.includes(invoice.id) ? <Loader2 size={16} className="animate-spin text-orange-400" /> : <Download size={16} />}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Vista escritorio */}
      <div className="hidden md:block flex-1 overflow-auto bg-slate-900/90 rounded-3xl shadow-2xl border border-slate-700/50 relative w-full">
        <table className="min-w-max w-full border-separate border-spacing-0">
          <thead className="bg-slate-900/95 backdrop-blur-sm">
            <tr>
              <th className="px-4 py-3 text-center bg-slate-900/95 sticky left-0 z-50 border-b border-slate-700/50">
                <input type="checkbox" onChange={handleSelectAll} checked={selectablePool.length > 0 && selectedIds.length === selectablePool.length} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500/40 focus:ring-offset-0" />
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 sticky left-[48px] z-50 border-b border-r border-slate-700/50 shadow-[2px_0_8px_-4px_rgba(0,0,0,0.6)]">Expediente</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Abogado</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Aseguradora</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Hon. Abg.</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Hon. Firma</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Kilometraje</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Tipo Servicio</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Ciudad</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Cliente Corp.</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Cuenta</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Estado</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Fecha</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Liberación</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 border-b border-slate-700/50">Fecha Aud./Lib.</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-900/95 sticky right-0 z-50 border-b border-l border-slate-700/50 shadow-[-2px_0_8px_-4px_rgba(0,0,0,0.6)]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (<tr><td colSpan="16" className="text-center py-12 text-slate-500 text-sm">No hay facturas.</td></tr>) : (
              invoices.map((invoice) => {
                const rowBg = selectedIds.includes(invoice.id) ? 'bg-blue-500/10' : 'bg-slate-900/40';
                return (
                <tr key={invoice.id} className={`group hover:bg-slate-800/60 transition-colors ${selectedIds.includes(invoice.id) ? 'bg-blue-500/10' : ''}`}>
                  <td className={`px-4 py-3 text-center sticky left-0 z-40 ${rowBg} group-hover:bg-slate-800/60 transition-colors border-b border-slate-800/60`}><input type="checkbox" checked={selectedIds.includes(invoice.id)} onChange={() => handleSelectOne(invoice.id)} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500/40 focus:ring-offset-0" /></td>
                  <td className={`px-4 py-3 whitespace-nowrap text-xs font-semibold text-blue-300 sticky left-[48px] z-40 ${rowBg} group-hover:bg-slate-800/60 transition-colors border-r border-b border-slate-800/60 shadow-[2px_0_8px_-4px_rgba(0,0,0,0.6)]`}>{invoice.expediente}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 border-b border-slate-800/60">{invoice.abogado || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 border-b border-slate-800/60">{invoice.cliente || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-200 tabular-nums text-right border-b border-slate-800/60">{formatCurrency(invoice.honorario_abogado)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-200 tabular-nums text-right border-b border-slate-800/60">{formatCurrency(invoice.honorario_firma)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 tabular-nums text-right border-b border-slate-800/60">{formatCurrency(invoice.kilometraje ?? 0)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 border-b border-slate-800/60">{invoice.tipo_servicio || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 border-b border-slate-800/60">{invoice.ciudad || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-300 border-b border-slate-800/60">{invoice.cliente_corporativo || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 border-b border-slate-800/60">{invoice.cuenta || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs border-b border-slate-800/60"><span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${invoice.estadoColor}`}>{invoice.estado}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 tabular-nums border-b border-slate-800/60">{invoice.vencimiento || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 border-b border-slate-800/60">{invoice.liberacion || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400 tabular-nums border-b border-slate-800/60">{invoice.fecha_sesion || '—'}</td>
                  <td className={`px-4 py-3 whitespace-nowrap text-right text-xs font-medium sticky right-0 z-40 ${rowBg} group-hover:bg-slate-800/60 transition-colors border-l border-b border-slate-800/60 shadow-[-2px_0_8px_-4px_rgba(0,0,0,0.6)]`}>
                    <div className="flex items-center justify-end gap-1.5">
                      {invoice.pagado_en ? (
                        <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] font-semibold px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full whitespace-nowrap" title={`Pagado el ${new Date(invoice.pagado_en).toLocaleDateString('es-CO')}${invoice.pagado_por_nickname ? ' por ' + invoice.pagado_por_nickname : ''}`}>
                          <Lock size={12} /> Pagado
                        </span>
                      ) : (
                        <>
                          <button onClick={() => onEdit(invoice)} className="p-1.5 rounded-lg text-blue-300 hover:text-white hover:bg-blue-500/15 transition-colors" title="Editar"><FileEdit size={16} /></button>
                          <button onClick={() => onDelete(invoice)} className="p-1.5 rounded-lg text-rose-400 hover:text-white hover:bg-rose-500/15 transition-colors" title="Eliminar"><Trash2 size={16} /></button>
                        </>
                      )}
                      <button onClick={() => onHistory(invoice)} className="p-1.5 rounded-lg text-violet-300 hover:text-white hover:bg-violet-500/15 transition-colors" title="Ver historial"><History size={16} /></button>
                      <button onClick={() => onGenerateSingle(invoice)} disabled={generatingIds.includes(invoice.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50" title="Cuenta de cobro">{generatingIds.includes(invoice.id) ? <Loader2 size={16} className="animate-spin text-orange-400" /> : <Download size={16} />}</button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===============================================================
// COMPONENTE PRINCIPAL
// ===============================================================
const Facturacion = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingIds, setGeneratingIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroAbogado, setFiltroAbogado] = useState('todos');
  const [filtroClienteCorp, setFiltroClienteCorp] = useState('todos');
  const [filtroTipoServicio, setFiltroTipoServicio] = useState('todos');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [auditExpediente, setAuditExpediente] = useState(null);
  const [clientesConfig, setClientesConfig] = useState([]);
  const [abogados, setAbogados] = useState([]);
  const [showHonorarios, setShowHonorarios] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 200;

  // Cargar lista de aseguradoras configuradas
  useEffect(() => {
    const fetchClientesConfig = async () => {
      try {
        const clientes = await api.get('/clientes-config');
        setClientesConfig(Array.isArray(clientes) ? clientes : []);
      } catch (e) {
        console.warn('[Facturacion] No se pudo cargar clientes-config:', e);
        setClientesConfig([]);
      }
    };
    fetchClientesConfig();
  }, []);

  // Cargar abogados
  useEffect(() => {
    const fetchAbogados = async () => {
      try {
        const data = await api.get('/usuarios');
        const lista = Array.isArray(data) ? data : [];
        const filtrados = lista.filter(u => {
          const rol = (u.rol || '').toLowerCase();
          return rol.includes('abogado');
        });
        setAbogados(filtrados);
      } catch (e) {
        console.warn('[Facturacion] No se pudo cargar abogados:', e);
        setAbogados([]);
      }
    };
    fetchAbogados();
  }, []);

  const resolveAseguradoraKey = useCallback((nombre) => {
    if (!nombre) return 'desconocida';
    const lowerName = nombre.toLowerCase().trim();
    const match = clientesConfig.find(c => {
      const configName = (c.nombre_display || c.nombre || '').toLowerCase().trim();
      return configName === lowerName || lowerName.includes(configName) || configName.includes(lowerName);
    });
    if (match) return (match.nombre_display || match.nombre || '').toLowerCase().trim();
    if (lowerName.includes('ike')) return 'ike';
    if (lowerName.includes('asissprex') || lowerName.includes('assisprex')) return 'asissprex';
    return lowerName;
  }, [clientesConfig]);

  const normalizeAseguradora = (nombre) => {
    if (!nombre) return nombre;
    const lower = nombre.toLowerCase().trim();
    if (lower.includes('assisprex') || lower.includes('asissprex')) return 'ASSISPREX';
    return nombre;
  };

  const formatInvoiceData = useCallback((data) => {
    return (Array.isArray(data) ? data : []).map((caso, index) => {
      const idDelBack = caso.cierre_servicio;
      const match = OPCIONES_CIERRE_MASTER.find(opt => opt.id == idDelBack);
      const estadoTexto = match ? match.label : (idDelBack || 'Pendiente');
      const honorarioAbogado = caso.honorario_abogado || 0;
      const honorarioFirma = caso.honorario_firma || 0;
      const montoNum = honorarioAbogado + honorarioFirma;
      return {
        id: `FC-${caso.expediente}-${index}`,
        abogado: caso.abogado || null,
        cliente: normalizeAseguradora(caso.aseguradora),
        monto: formatCurrency(montoNum),
        montoNum,
        honorario_abogado: honorarioAbogado,
        honorario_firma: honorarioFirma,
        kilometraje: caso.kilometraje ?? 0,
        estado: estadoTexto,
        cierre_servicio: idDelBack,
        estadoColor: getStatusColor(estadoTexto),
        vencimiento: caso.fecha,
        expediente: caso.expediente,
        ciudad: caso.ciudad?.trim() || 'Sin ciudad',
        cliente_corporativo: (caso.cliente_corporativo?.trim() || 'N/A').toUpperCase(),
        cuenta: caso.cuenta?.trim() || 'N/A',
        tipo_servicio: caso.tipo_servicio?.trim() || 'N/A',
        liberacion: caso.liberacion || '',
        fecha_sesion: caso.fecha_sesion || null,
        pagado_en: caso.pagado_en || null,
        pagado_por_nickname: caso.pagado_por_nickname || null,
      };
    });
  }, []);

  // Race-guard: cada fetch aborta el anterior. Sin esto, un request lento (p.ej.
  // un estado intermedio al elegir fechas que bajaba toda la base) resolvía DESPUÉS
  // de uno nuevo y rápido, sobrescribiendo datos buenos con "Error al cargar facturas."
  const invoicesAbortRef = React.useRef(null);

  const fetchInvoices = useCallback(async (fechaDesde, fechaHasta) => {
    if (invoicesAbortRef.current) invoicesAbortRef.current.abort();
    const controller = new AbortController();
    invoicesAbortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (!fechaDesde && !fechaHasta) {
        const now = new Date();
        params.append('mes', `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
      }
      const qs = params.toString();
      const data = await api.get(`/factura${qs ? '?' + qs : ''}`, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setInvoices(formatInvoiceData(data));
    } catch (err) {
      // Un request cancelado (reemplazado por otro más nuevo) NO es un error visible.
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setError("Error al cargar facturas.");
    } finally {
      if (invoicesAbortRef.current === controller) setLoading(false);
    }
  }, [formatInvoiceData]);

  // Búsqueda por expediente con debounce
  const prevSearchRef = React.useRef('');
  useEffect(() => {
    const term = searchTerm.trim();
    if (term === '' && prevSearchRef.current !== '') {
      prevSearchRef.current = '';
      fetchInvoices(filtroFechaDesde, filtroFechaHasta);
      return;
    }
    prevSearchRef.current = term;
    if (term.length < 3) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.get(`/factura?expediente=${encodeURIComponent(term)}`);
        const results = formatInvoiceData(data);
        if (results.length > 0) setInvoices(results);
      } catch (err) { /* silenciar */ }
      finally { setLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, formatInvoiceData, fetchInvoices, filtroFechaDesde, filtroFechaHasta]);

  // Carga inicial
  useEffect(() => { fetchInvoices('', ''); }, [fetchInvoices]);

  // Re-fetch al cambiar fechas (con debounce: al elegir desde y luego hasta, no
  // se dispara el request intermedio; el AbortController cubre cualquier solape).
  useEffect(() => {
    if (!filtroFechaDesde && !filtroFechaHasta) return;
    const timer = setTimeout(() => {
      fetchInvoices(filtroFechaDesde, filtroFechaHasta);
    }, 500);
    return () => clearTimeout(timer);
  }, [filtroFechaDesde, filtroFechaHasta, fetchInvoices]);

  const handleGenerateSingleCuentaCobro = async (invoice) => {
    setGeneratingIds(prev => [...prev, invoice.id]);
    try {
      const blob = await api.post('/cuenta-cobro/generar', { expedientes: [{ expediente: invoice.expediente, aseguradora: invoice.cliente }] }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cuenta_Cobro_${invoice.expediente}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addLog(`Cuenta de cobro generada — exp. ${invoice.expediente}`, 'success', 'Facturación');
    } catch (err) {
      addLog(`Error al generar cuenta de cobro — exp. ${invoice.expediente}`, 'error', 'Facturación');
      alert("Error al generar el PDF.");
    } finally {
      setGeneratingIds(prev => prev.filter(id => id !== invoice.id));
    }
  };

  const handleGenerateSelected = async () => {
    if (selectedIds.length === 0) return;
    setIsBulkGenerating(true);
    try {
      const seleccionados = invoices.filter(inv => selectedIds.includes(inv.id));
      const blob = await api.post('/cuenta-cobro/generar', { expedientes: seleccionados.map(inv => ({ expediente: inv.expediente, aseguradora: inv.cliente })) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Cuentas_Cobro_Masivas.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addLog(`Cuenta de cobro masiva generada — ${seleccionados.length} expedientes`, 'success', 'Facturación');
    } catch (err) {
      addLog(`Error al generar cuenta de cobro masiva`, 'error', 'Facturación');
      alert("Error al generar las cuentas masivas.");
    } finally {
      setIsBulkGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (selectedIds.length === 0) return;
    setIsSendingEmail(true);
    try {
      const seleccionados = invoices.filter(inv => selectedIds.includes(inv.id));
      const res = await api.post('/cuenta-cobro/enviar-email', {
        expedientes: seleccionados.map(inv => ({ expediente: inv.expediente, aseguradora: inv.cliente })),
      });
      addLog(`Cuenta de cobro enviada por email a ${res.destinatario || res.abogado}`, 'success', 'Facturación');
      alert(`✓ Correo enviado a ${res.destinatario || res.abogado}`);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Error al enviar el correo';
      addLog(`Error al enviar cuenta de cobro por email: ${msg}`, 'error', 'Facturación');
      alert(`Error: ${msg}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEditInvoice = (invoiceToEdit) => { setCurrentInvoice(invoiceToEdit); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setCurrentInvoice(null); };

  const handleSaveModal = async (updatedFormData) => {
    if (!currentInvoice) return;
    const { cliente, expediente, id } = currentInvoice;
    const aseguradoraKey = resolveAseguradoraKey(cliente);
    const idEstado = updatedFormData.cierre_servicio;
    const estadoObj = OPCIONES_CIERRE_MASTER.find(opt => opt.id == idEstado);
    const textoEstadoParaUI = estadoObj ? estadoObj.label : (idEstado ? String(idEstado) : 'PENDIENTE');

    // Resolver nombre del abogado para mostrar en tabla
    const abogadoObj = abogados.find(a => String(a.num_doc) === String(updatedFormData.abogado));
    const abogadoNombre = abogadoObj ? abogadoObj.nombre : (updatedFormData.abogado || null);

    // Convertir fecha_sesion de "T" format a espacio para la DB
    const fechaSesionDB = updatedFormData.fecha_sesion
      ? updatedFormData.fecha_sesion.replace('T', ' ')
      : null;

    const honAbogado = parseFloat(updatedFormData.honorario_abogado) || 0;
    const honFirma   = parseFloat(updatedFormData.honorario_firma)   || 0;
    const newMontoNum = honAbogado + honFirma;

    // tipo_servicio: viene como label desde el select, necesitamos el ID para el backend
    // Filtrar por grupo de la aseguradora para obtener el ID correcto (IKE=101-106, ASIS=201-206)
    const tipoLabel = updatedFormData.tipo_servicio || (currentInvoice.tipo_servicio !== 'N/A' ? currentInvoice.tipo_servicio : '');
    const clienteUpper = (cliente || '').toUpperCase();
    const esIKE = clienteUpper.includes('IKE');
    const esASIS = clienteUpper.includes('ASIS') || clienteUpper.includes('SPREX');
    const tipoOpt = tipoLabel
      ? OPCIONES_TIPO_SERVICIO.find(opt => {
          if (opt.label !== tipoLabel) return false;
          if (esIKE) return opt.grupo === 'IKE' || opt.grupo === 'COMUN';
          if (esASIS) return opt.grupo === 'ASIS' || opt.grupo === 'COMUN';
          return true;
        }) || OPCIONES_TIPO_SERVICIO.find(opt => opt.label === tipoLabel)
      : null;
    const idTipo = tipoOpt ? tipoOpt.id : null;

    const payload = {
      honorario_abogado_local:   honAbogado,
      honorario_abogado_foraneo: honAbogado,
      honorario_firma:           honFirma,
      kilometraje: parseFloat(updatedFormData.kilometraje) || 0,
      cierre_servicio: idEstado,
      id_cierre_se: idEstado,
      descripcion_cierre: textoEstadoParaUI,
      status_label: textoEstadoParaUI,
      abogado: updatedFormData.abogado,
      liberacion: updatedFormData.liberacion || '',
      fecha_sesion: fechaSesionDB,
      ...(idTipo !== null && { tipo_servicio: idTipo }),
    };

    try {
      await api.put(`/casos/${aseguradoraKey}/${expediente}`, payload);
      addLog(`Expediente ${expediente} actualizado — estado: ${textoEstadoParaUI}`, 'success', 'Facturación');
      // Actualizar estado local inmediatamente para reflejar cambios sin recargar
      setInvoices(prev => prev.map(inv => inv.id === id ? {
        ...inv,
        monto: formatCurrency(newMontoNum),
        montoNum: newMontoNum,
        honorario_abogado: honAbogado,
        honorario_firma: honFirma,
        kilometraje: payload.kilometraje,
        estado: textoEstadoParaUI,
        estadoColor: getStatusColor(textoEstadoParaUI),
        cierre_servicio: idEstado,
        abogado: abogadoNombre,
        liberacion: payload.liberacion,
        fecha_sesion: payload.fecha_sesion,
        tipo_servicio: tipoLabel,
      } : inv));
      handleCloseModal();
    } catch (err) {
      addLog(`Error al actualizar expediente ${expediente}`, 'error', 'Facturación');
      alert("Error al actualizar.");
    }
  };

  const handleDeleteInvoice = async (invoiceToDelete) => {
    const { cliente, expediente, id } = invoiceToDelete;
    const aseguradoraKey = resolveAseguradoraKey(cliente);
    if (window.confirm(`¿Eliminar caso ${expediente}?`)) {
      try {
        await api.delete(`/casos/${aseguradoraKey}/${expediente}`);
        setInvoices(prev => prev.filter(inv => inv.id !== id));
        addLog(`Expediente ${expediente} eliminado`, 'warning', 'Facturación');
      } catch (err) {
        addLog(`Error al eliminar expediente ${expediente}`, 'error', 'Facturación');
        alert("No se pudo eliminar.");
      }
    }
  };

  const filterOptions = useMemo(() => {
    const getUnique = (key) => [...new Set(invoices.map(inv => inv[key]).filter(Boolean))].sort();
    return {
      estados: getUnique('estado'),
      abogados: [...new Set(invoices.map(inv => inv.abogado || 'No asignado'))].sort(),
      clientesCorp: getUnique('cliente_corporativo'),
      tiposServicio: getUnique('tipo_servicio'),
    };
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const term = normalizeString(searchTerm);
    return invoices.filter(inv => {
      if (filtroEstado !== 'todos' && inv.estado !== filtroEstado) return false;
      if (filtroAbogado !== 'todos' && (inv.abogado || 'No asignado') !== filtroAbogado) return false;
      if (filtroClienteCorp !== 'todos' && inv.cliente_corporativo !== filtroClienteCorp) return false;
      if (filtroTipoServicio !== 'todos' && inv.tipo_servicio !== filtroTipoServicio) return false;
      if (filtroFechaDesde && inv.vencimiento < filtroFechaDesde) return false;
      if (filtroFechaHasta && inv.vencimiento > filtroFechaHasta) return false;
      if (term !== '') return (
        normalizeString(inv.id).includes(term) ||
        normalizeString(inv.abogado).includes(term) ||
        normalizeString(inv.cliente).includes(term) ||
        normalizeString(inv.cliente_corporativo).includes(term) ||
        normalizeString(inv.expediente).includes(term)
      );
      return true;
    });
  }, [invoices, searchTerm, filtroEstado, filtroAbogado, filtroClienteCorp, filtroTipoServicio, filtroFechaDesde, filtroFechaHasta]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filtroEstado, filtroAbogado, filtroClienteCorp, filtroTipoServicio, filtroFechaDesde, filtroFechaHasta]);

  const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const filteredTotals = useMemo(() => {
    let fact = 0; let pend = 0;
    filteredInvoices.forEach(inv => {
      fact += inv.montoNum;
      if (!String(inv.estado).toLowerCase().includes('exitoso') && !String(inv.estado).toLowerCase().includes('realizada')) {
        pend += inv.montoNum;
      }
    });
    return { ingresosFiltrados: fact, pendientesFiltrados: pend };
  }, [filteredInvoices]);

  const handleExportExcel = () => {
    if (filteredInvoices.length === 0) return;
    const dataToExport = filteredInvoices.map(inv => ({
      "Expediente": inv.expediente,
      "Fecha": inv.vencimiento,
      "Abogado": inv.abogado || 'No asignado',
      "Aseguradora": inv.cliente,
      "Cliente Corporativo": inv.cliente_corporativo,
      "Tipo de Servicio": inv.tipo_servicio,
      "Honorario Abogado": inv.honorario_abogado,
      "Honorario Firma": inv.honorario_firma,
      "Kilometraje": inv.kilometraje ?? 0,
      "Estado": inv.estado,
      "Liberación": inv.liberacion || '',
      "Ciudad": inv.ciudad,
      "Cuenta": inv.cuenta,
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturación Filtrada");
    XLSX.writeFile(wb, `Reporte_Facturacion_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const selectClass = 'w-full rounded-xl bg-slate-800 border border-slate-700 text-slate-100 px-3 py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/70 transition-all';
  const labelClass = 'block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5';
  const activeFilters = [filtroEstado, filtroAbogado, filtroClienteCorp, filtroTipoServicio].filter(v => v !== 'todos').length + (filtroFechaDesde ? 1 : 0) + (filtroFechaHasta ? 1 : 0);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 to-slate-900 text-slate-100 p-4 lg:p-5 gap-4 overflow-hidden">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-none">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Facturación</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {loading ? 'Cargando...' : `${filteredInvoices.length} factura${filteredInvoices.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowHonorarios(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-100 text-sm font-semibold border border-slate-700/60 hover:border-slate-600 shadow-lg shadow-slate-950/40 transition-all active:scale-95"
          >
            <Settings size={16} /> Honorarios
          </button>
          <button onClick={handleExportExcel}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 text-white text-sm font-semibold shadow-lg shadow-emerald-950/40 transition-all active:scale-95">
            <FileSpreadsheet size={16} /> Exportar
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="flex-none grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-slate-800/40 rounded-2xl p-4 border border-slate-700/40 hover:border-slate-600/60 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-blue-500/10 flex items-center justify-center"><DollarSign size={15} className="text-blue-400" /></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold">Total Facturado</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-white tabular-nums leading-none">{loading ? '—' : formatCurrency(filteredTotals.ingresosFiltrados)}</p>
        </div>
        <div className="bg-slate-800/40 rounded-2xl p-4 border border-slate-700/40 hover:border-slate-600/60 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center"><FileSpreadsheet size={15} className="text-emerald-400" /></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold">% Cobrado</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-white tabular-nums leading-none">
            {loading || filteredInvoices.length === 0
              ? '—'
              : `${Math.round((filteredInvoices.filter(i => i.pagado_en).length / filteredInvoices.length) * 100)}%`}
          </p>
        </div>
        <div className="bg-slate-800/40 rounded-2xl p-4 border border-slate-700/40 hover:border-slate-600/60 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center"><Clock size={15} className="text-amber-400" /></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold">Pendientes</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-white tabular-nums leading-none">{loading ? '—' : formatCurrency(filteredTotals.pendientesFiltrados)}</p>
        </div>
        <div className="bg-slate-800/40 rounded-2xl p-4 border border-slate-700/40 hover:border-slate-600/60 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center"><FileEdit size={15} className="text-violet-400" /></div>
            <span className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold">Registros</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-white tabular-nums leading-none">{loading ? '—' : filteredInvoices.length.toLocaleString('es-CO')}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex-none rounded-2xl bg-slate-800/40 border border-slate-700/40 p-3">
        <div className="relative">
          <input type="text" placeholder="Buscar expediente, abogado, aseguradora..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        </div>
        <details className="mt-3 group">
          <summary className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none flex items-center gap-1 hover:text-slate-300 transition-colors">
            <span className="group-open:hidden">Mostrar filtros</span>
            <span className="hidden group-open:inline">Ocultar filtros</span>
            <span className="text-slate-600 ml-1">({activeFilters} activos)</span>
          </summary>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-3 items-end">
            <div><label className={labelClass}>Estado</label><select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} className={selectClass}><option value="todos">Todos</option>{filterOptions.estados.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label className={labelClass}>Abogado</label><select value={filtroAbogado} onChange={e => setFiltroAbogado(e.target.value)} className={selectClass}><option value="todos">Todos</option>{filterOptions.abogados.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label className={labelClass}>Cliente Corp.</label><select value={filtroClienteCorp} onChange={e => setFiltroClienteCorp(e.target.value)} className={selectClass}><option value="todos">Todos</option>{filterOptions.clientesCorp.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label className={labelClass}>Servicio</label><select value={filtroTipoServicio} onChange={e => setFiltroTipoServicio(e.target.value)} className={selectClass}><option value="todos">Todos</option>{filterOptions.tiposServicio.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div><label className={labelClass}>Desde</label><input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} className={selectClass} /></div>
            <div><label className={labelClass}>Hasta</label><input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} className={selectClass} /></div>
          </div>
        </details>
      </div>

      {/* Tabla */}
      <div className="flex-1 min-h-0 relative flex flex-col">
        {loading && <div className="flex justify-center items-center h-64"><Loader2 size={40} className="animate-spin text-blue-400" /></div>}
        {error && <div className="flex items-center justify-center h-64 text-red-400"><AlertTriangle size={20} className="mr-2" /> {error}</div>}
        {!loading && !error && (<>
          <div className="flex-1 min-h-0 overflow-auto">
            <InvoicesTable
              invoices={paginatedInvoices}
              allInvoices={filteredInvoices}
              onEdit={handleEditInvoice}
              onDelete={handleDeleteInvoice}
              onGenerateSingle={handleGenerateSingleCuentaCobro}
              onHistory={(inv) => setAuditExpediente(inv.expediente)}
              generatingIds={generatingIds}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t border-slate-700/50 bg-slate-900/80 flex-shrink-0">
              <span className="text-xs text-slate-400 text-center sm:text-left">
                {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredInvoices.length)} de {filteredInvoices.length} facturas
              </span>
              <div className="flex items-center justify-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">&laquo;</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">&lsaquo;</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  const page = start + i;
                  return page <= totalPages ? (
                    <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>{page}</button>
                  ) : null;
                })}
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">&rsaquo;</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">&raquo;</button>
              </div>
            </div>
          )}
        </>)}
      </div>

      {/* Bulk action bar flotante — aparece cuando hay selección */}
      {selectedIds.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-slate-900/95 border border-slate-700/80 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.9)] backdrop-blur-md"
          style={{ animation: 'bulkBarIn 280ms cubic-bezier(0.16, 1, 0.3, 1) both' }}
          role="region"
          aria-label="Acciones por lote"
        >
          <style>{`@keyframes bulkBarIn { from { opacity: 0; transform: translate(-50%, 1rem); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
          <span className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl bg-blue-500/10 border border-blue-500/30">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-[11px] font-bold text-blue-200 tabular-nums tracking-wider uppercase">{selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}</span>
          </span>
          <div className="w-px h-6 bg-slate-700/70" />
          <button
            onClick={handleGenerateSelected}
            disabled={isBulkGenerating}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-500/90 hover:bg-orange-400 text-white text-xs font-semibold shadow-lg shadow-orange-950/40 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isBulkGenerating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Cuenta de cobro
          </button>
          <button
            onClick={handleSendEmail}
            disabled={isSendingEmail}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600/90 hover:bg-blue-500 text-white text-xs font-semibold shadow-lg shadow-blue-950/40 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            Enviar email
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="ml-1 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            title="Limpiar selección"
            aria-label="Limpiar selección"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Modal edición */}
      {isModalOpen && currentInvoice && (
        <EditInvoiceModal
          invoice={currentInvoice}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
          clientesConfig={clientesConfig}
          abogados={abogados}
        />
      )}

      {/* Modal historial (audit log) */}
      <AuditLogModal
        isOpen={!!auditExpediente}
        expediente={auditExpediente}
        onClose={() => setAuditExpediente(null)}
      />

      {/* Modal honorarios */}
      {showHonorarios && (
        <HonorariosModal
          onClose={() => setShowHonorarios(false)}
          clientesConfig={clientesConfig}
        />
      )}
    </div>
  );
};

export default Facturacion;
